var FenwickTree          = require("enyo-ui/FenwickTree"),
    RepeaterListArranger = require("enyo-ui/RepeaterListArranger"),
    kind                 = require("enyo/kind");

require("enyo-ui");

/**
 * [RepeaterPaddedListArranger]{@link enyo-ui.RepeaterPaddedListArranger} is an
 * extension of [RepeaterListArranger]{@link enyo-ui.RepeaterListArranger}
 * implementing the ability to configure a spacing between the items and a
 * padding around the list.
 *
 * @ui
 * @class   enyo-ui.RepeaterPaddedListArranger
 * @extends enyo-ui.RepeaterListArranger
 * @public
 */
module.exports = kind(/** @lends  enyo-ui.RepeaterPaddedListArranger.prototype */ {

    /**
     * @private
     */
    name: "enyo-ui.RepeaterPaddedListArranger",

    /**
     * @private
     */
    kind: RepeaterListArranger,

    /**
     * @private
     */
    options: /** @lends  enyo-ui.RepeaterListArranger.prototype */ {

        /**
         * The amount of spacing between children.
         *
         * @type {number}
         * @default 0
         * @public
         */
        childSpacing: 0,

        /**
         * The amount of padding before the first child.
         *
         * @return {number}  The padding
         * @public
         */
        padBefore: function() {
            return this.getOption("childSpacing");
        },

        /**
         * The amount of padding after the last child.
         *
         * @return {number}  The padding
         * @public
         */
        padAfter: function() {
            return this.getOption("childSpacing");
        }
    },

    /**
     * Instantiate a FenwickTree to manage the sizes of list children.
     *
     * @private
     */
    createChildSizeManager: function() {
        this.childSizeManager = new FenwickTree({
            defaultValue: this.getOption("defaultChildSize") + this.getOption("childSpacing"),
            maxLength:    this.getOption("maxLength")
        });
    },

    /**
     * Calculates the properties of the list arrangement.
     *
     * @public
     */
    reflow: function() {
        var childContainer = this.repeater.getChildContainer(),
            direction      = this.getOption("direction"),
            childSpacing   = this.getOption("childSpacing");

        // Container required
        if (!childContainer.hasNode()) {
            return;
        }

        // If we're using placeholders, use placeholderDimensions as
        // defaultChildSize.
        if (this.layoutPlaceholder) {

            // Calculate the dimensions of the placeholder
            this.calcPlaceholderDimensions();

            // Reset the childSizeManager to use the placeholderDimensions
            this.childSizeManager.reset(direction === "vertical"
                ? this.placeholderHeight + childSpacing
                : this.placeholderWidth + childSpacing);
        }

        // No placeholders, just use defaultChildSize option.
        else {
            this.childSizeManager.reset(this.getOption("defaultChildSize") + childSpacing);
        }
    },

    /**
     * Gets the amount of placeholders needed to fill a viewport of the provided
     * dimensions.
     *
     * @param  {number}  width   The width
     * @param  {number}  height  The height
     * @return {number}  The amount of placeholders.
     * @public
     */
    getPlaceholderCount: function(width, height) {
        var direction         = this.getOption("direction"),
            placeholderWidth  = this.placeholderWidth,
            placeholderHeight = this.placeholderHeight,
            childSpacing      = this.getOption("childSpacing");

        // Enough to fill the viewport height, plus one extra in case of
        // scrolling.
        if (direction === "vertical" && placeholderHeight > 0) {
            return Math.min(
                this.repeater.length,
                Math.ceil(height / (placeholderHeight + childSpacing)) + 1
            );
        }

        // Enough to fill the viewport width, plus one extra in case of
        // scrolling.
        if (direction === "horizontal" && placeholderWidth > 0) {
            return Math.min(
                this.repeater.length,
                Math.ceil(width / (placeholderWidth + childSpacing)) + 1
            );
        }

        // No placeholder dimensions: no placeholders.
        return 0;
    },

    /**
     * Get the size of the entire arrangement. Should have at least a width or
     * height property.
     *
     * @return {Object}  The arrangement size.
     * @public
     */
    getArrangementSize: function() {
        var length = this.repeater.length,
            padBefore,
            padAfter,
            childSpacing,
            size   = {},
            property,
            value  = 0;

        if (length) {
            padBefore    = this.getOption("padBefore");
            padAfter     = this.getOption("padAfter");
            childSpacing = this.getOption("childSpacing");
            value        = this.childSizeManager.getSum(length)
                - childSpacing
                + padBefore
                + padAfter;
        }

        property       = this.getOption("direction") === "vertical" ? "height" : "width";
        size[property] = value;

        return size;
    },

    /**
     * Gets a sorted set of indexes of children within the arrangement such that
     * either the child itself ór half of the childSpacing around it intersect
     * with the provided bounds.
     *
     * The incoming bounds should be defined in pixels, relative to the top-left
     * corner of the childContainer.
     *
     * @param  {Object}  bounds         The bounds
     * @param  {number}  bounds.top     The top edge of the bounding box in
     *                                  pixels from the top of the grid
     * @param  {number}  bounds.left    The left edge of the bounding box in
     *                                  pixels from the left of the grid
     * @param  {number}  bounds.right   The right edge of the bounding box in
     *                                  pixels from the left of the grid
     * @param  {number}  bounds.bottom  The bottom edge of the bounding box in
     *                                  pixels from the top of the grid
     * @return {Array}   Array of indexes
     * @method
     * @public
     */
    getArrangementIndexes: kind.inherit(function(sup) {
        return function(bounds) {
            var padBefore    = this.getOption("padBefore"),
                childSpacing = this.getOption("childSpacing");

            // Offset the bounds to simulate a list with no padding and with
            // half of the childSpacing placed before and after each child.
            if (this.getOption("direction") === "vertical") {
                bounds.top    = bounds.top - padBefore + (childSpacing / 2);
                bounds.bottom = bounds.bottom - padBefore + (childSpacing / 2);
            }
            else {
                bounds.left  = bounds.left - padBefore + (childSpacing / 2);
                bounds.right = bounds.right - padBefore + (childSpacing / 2);
            }

            // Super method can now handle the rest
            return sup.call(this, bounds);
        };
    }),

    /**
     * Returns the distance (in pixels) between the top edge of the child
     * control at the given index, and the top edge of the childContainer.
     *
     * @param  {number}  index  The index
     * @return {number}  The offset
     * @method
     * @public
     */
    getOffsetTop: kind.inherit(function(sup) {
        return function(index) {
            var offset = sup.call(this, index);

            if (this.getOption("direction") === "vertical") {
                return offset + this.getOption("padBefore");
            }
            else {
                return offset;
            }
        };
    }),

    /**
     * Returns the distance (in pixels) between the left edge of the child
     * control at the given index, and the left edge of the childContainer.
     *
     * @param  {number}  index  The index
     * @return {number}  The offset
     * @method
     * @public
     */
    getOffsetLeft: kind.inherit(function(sup) {
        return function(index) {
            var offset = sup.call(this, index);

            if (this.getOption("direction") === "horizontal") {
                return offset + this.getOption("padBefore");
            }
            else {
                return offset;
            }
        };
    }),

    /**
     * Gets the index of the child positioned closest to the provided
     * coordinates. If the provided coordinates are located in the spacing
     * between children, the index positioned to the bottom/right is returned.
     *
     * The incoming coordinates should be defined in pixels, relative to the
     * top-left corner of the childContainer.
     *
     * @param  {number}  x  The coordinates on the `x` axis.
     * @param  {number}  y  The coordinates on the `y` axis.
     * @return {number}  The index
     * @method
     * @public
     */
    getNearestIndex: kind.inherit(function(sup) {
        return function(x, y) {
            var padBefore    = this.getOption("padBefore"),
                childSpacing = this.getOption("childSpacing");

            // Offset the coordinates to simulate a grid with no padding and all
            // childSpacing applied before the children.
            if (this.getOption("direction") === "horizontal") {
                x = x - padBefore + childSpacing;
            }
            else {
                y = y - padBefore + childSpacing;
            }

            // Super method can now handle the rest
            return sup.call(this, x, y);
        };
    }),

    /**
     * Returns the scroll offset (in pixels) that would align the top edge of
     * the child arranged at the provided index exactly one `childSpacing`
     * below/to the right of a scrolled view of the childContainer.
     *
     * @param  {number}  index  The index
     * @return {number}  The offset
     * @public
     */
    getScrollTop: function(index) {
        var offset = this.getOffsetTop(index);

        if (this.getOption("direction") === "vertical") {
            return offset - this.getOption(index === 0 ? "padBefore" : "childSpacing");
        }
        else {
            return offset;
        }
    },

    /**
     * Returns the scroll offset (in pixels) that would align the left edge of
     * the child arranged at the provided index exactly one `colSpacing` to the
     * right of the left edge of a scrolled view of the childContainer.
     *
     * @param  {number}  index  The index
     * @return {number}  The offset
     * @public
     */
    getScrollLeft: function(index) {
        var offset = this.getOffsetLeft(index);

        if (this.getOption("direction") === "horizontal") {
            return offset - this.getOption(index === 0 ? "padBefore" : "childSpacing");
        }
        else {
            return offset;
        }
    },

    /**
     * Saves size of the child associated with the provided index. Returns
     * whether the size changed.
     *
     * @param  {number}   index  The index
     * @param  {number}   size   The size
     * @return {boolean}  `true` if size changed, `false` otherwise
     * @protected
     */
    setSizeAtIndex: function(index, size) {
        var oldSize = this.getSizeAtIndex(index);

        if (size > 0) {
            this.childSizeManager.setValue(index + 1, size + this.getOption("childSpacing"));
        }
        else {
            this.childSizeManager.setValue(index + 1, 0);
        }

        return oldSize !== size;
    },

    /**
     * Gets the size stored for the provided index, if any.
     *
     * @param  {number}  index  The index
     * @return {number}  The size
     * @protected
     */
    getSizeAtIndex: function(index) {
        var size = this.childSizeManager.getValue(index + 1);

        if (size === null) {
            size = this.childSizeManager.getDefaultValue();
        }

        return size > 0 ? Math.max(0, size - this.getOption("childSpacing")) : 0;
    }

});
